import { clientID, tenantID } from '@loop/supp-auth-bridging-basic';

/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: clientID, // This is the ONLY mandatory field that you need to supply.
    authority: `https://login.microsoftonline.com/${tenantID}`, // Defaults to "https://login.microsoftonline.com/common"
    redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    clientCapabilities: ['CP1'], // this lets the resource owner know that this client is capable of handling claims challenge.
  },
  cache: {
    cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: any, containsPii?: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
// export const protectedResources = {
//   api: {
//     staff: 'http://localhost:5000/api/staff',
//     product: 'http://localhost:5000/api/product',
//     developer: 'http://localhost:5000/api/developer',
//     admin: 'http://localhost:5000/api/admin',
//     scopes: [`api://${msalConfig.auth.clientId}/access_via_approle_assignments`],
//   },
// };

export const protectedResources = {
  api: {
    staff: '/api/staff',
    product: '/api/product',
    developer: '/api/developer',
    admin: '/api/admin',
    scopes: [`api://${msalConfig.auth.clientId}/access_via_approle_assignments`],
  },
};
/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [...protectedResources.api.scopes],
};

export const appRoles = {
  PRODUCT: 'Loop.Supp.Product',
  ADMIN: 'Loop.Supp.Admin',
  DEVELOPER: 'Loop.Supp.Developer',
  STAFF: 'Loop.Supp.Staff',
} as const;

export const cascadedRoles = {
  STAFF: [appRoles.STAFF, appRoles.PRODUCT, appRoles.DEVELOPER, appRoles.ADMIN],
  PRODUCT: [appRoles.PRODUCT, appRoles.DEVELOPER, appRoles.ADMIN],
  DEVELOPER: [appRoles.DEVELOPER, appRoles.ADMIN],
  ADMIN: [appRoles.ADMIN],
} as const;
